import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { AssistantMsg, detectCpt } from "@/pages/RevQ";
import "@/pages/askrevq.css";

// Floating "Ask revQ" dock — a bottom-right capsule that opens a compact chat card
// in the same corner. Mounted once at the app shell so it rides on every page.
// Hidden while a dialog / sheet / full-screen overlay is open. Conversation is kept
// for the session (this component never unmounts while authenticated).
const SUGGESTIONS = [
  "How can I bill 99213 to get paid?",
  "Best modifier for 99214 + 93000?",
  "Show me all details of 20610",
];

const CSS = `
.arq-fab{position:fixed;right:20px;bottom:20px;z-index:60;width:44px;height:44px;display:flex;align-items:center;gap:0;padding:0;justify-content:center;border:none;border-radius:22px;background:#141412;color:#fff;cursor:pointer;box-shadow:0 10px 28px rgba(20,20,18,.14);overflow:hidden;white-space:nowrap;transition:width .22s cubic-bezier(.4,0,.2,1),padding .22s cubic-bezier(.4,0,.2,1)}
.arq-fab:hover,.arq-fab:focus-visible{width:162px;padding:0 16px 0 12px;justify-content:flex-start;outline:none}
.arq-fab-mark{width:20px;height:20px;object-fit:contain;flex:none}
.arq-fab-label{opacity:0;max-width:0;overflow:hidden;font-size:13px;font-weight:600;line-height:1;transition:opacity .16s .04s,max-width .22s,margin .22s}
.arq-fab-kbd{opacity:0;max-width:0;overflow:hidden;font-size:10px;line-height:1;color:#A9A9A4;border:1px solid rgba(255,255,255,.22);border-radius:4px;padding:2px 4px;transition:opacity .16s .04s,max-width .22s,margin .22s}
.arq-fab:hover .arq-fab-label,.arq-fab:focus-visible .arq-fab-label{opacity:1;max-width:120px;margin-left:10px}
.arq-fab:hover .arq-fab-kbd,.arq-fab:focus-visible .arq-fab-kbd{opacity:1;max-width:40px;margin-left:auto}

.arq-card{position:fixed;right:20px;bottom:20px;z-index:60;width:min(380px,calc(100vw - 40px));height:min(540px,calc(100vh - 40px));background:#fff;border:1px solid #E9E9EC;border-radius:14px;box-shadow:0 10px 28px rgba(20,20,18,.14);display:flex;flex-direction:column;overflow:hidden;animation:arqrise .16s cubic-bezier(.3,.7,.3,1)}
@keyframes arqrise{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:none}}
.arq-card-head{display:flex;align-items:center;gap:10px;padding:12px 14px;border-bottom:1px solid #E9E9EC;flex:none}
.arq-head-mark{width:18px;height:18px;object-fit:contain}
.arq-head-title{font-size:13px;font-weight:700;color:#141412;flex:1}
.arq-head-x{width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#6e6e73;background:transparent;border:none;cursor:pointer}
.arq-head-x:hover{background:#F4F4F9;color:#141412}
.arq-arw{flex:1;min-height:0}
.arq-arw .ar-conv{padding:14px}
.arq-arw .ar-an-grid{grid-template-columns:1fr}
.arq-arw .ar-msg-a,.arq-arw .ar-an{max-width:100%}
`;

export default function AskRevqDock() {
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState({ overlay: false, exp: false });
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const convRef = useRef(null);

  const scrollDown = () =>
    requestAnimationFrame(() => {
      if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight;
    });

  const send = useCallback((textArg) => {
    const text = (textArg ?? "").trim();
    if (!text) return;
    const cpt = detectCpt(text);
    setMsgs((m) => [...m, { r: "u", t: text }, { r: "a", cpt }]);
    setInput("");
    scrollDown();
  }, []);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  // ⌘J / Ctrl+J toggles; Esc closes.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "j" || e.key === "J")) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Hide the dock while any dialog / sheet / full-screen overlay is open.
  // Some overlays (e.g. the Lens upload modal) use role="dialog" without a
  // data-state attribute, so match on role/aria-modal and a visibility check,
  // excluding this dock's own card.
  useEffect(() => {
    const check = () => {
      const nodes = document.querySelectorAll('[role="dialog"],[role="alertdialog"],[aria-modal="true"]');
      let found = false;
      nodes.forEach((n) => {
        if (n.closest('[data-testid="ask-revq-card"]')) return;
        if (n.offsetParent !== null || n.getClientRects().length) found = true;
      });
      setBlocked((b) => ({ ...b, overlay: found }));
    };
    const onExp = (e) => setBlocked((b) => ({ ...b, exp: !!e.detail?.open }));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-state", "role", "aria-modal", "style"] });
    window.addEventListener("revq:experience-state", onExp);
    return () => { mo.disconnect(); window.removeEventListener("revq:experience-state", onExp); };
  }, []);

  if (blocked.overlay || blocked.exp) return null;

  return (
    <>
      <style>{CSS}</style>
      {open ? (
        <div className="arq-card" data-testid="ask-revq-card" role="dialog" aria-label="Ask revQ">
          <div className="arq-card-head">
            <img src="/revq-emblem-black.png" alt="" className="arq-head-mark" />
            <span className="arq-head-title">Ask revQ</span>
            <button className="arq-head-x" onClick={() => setOpen(false)} data-testid="ask-revq-close" aria-label="Close Ask revQ">
              <X size={16} />
            </button>
          </div>
          <div className="arw arq-arw">
            <div className="ar-conv" ref={convRef} data-testid="ask-revq-conversation">
              {msgs.length ? (
                msgs.map((m, i) =>
                  m.r === "u" ? (
                    <div className="ar-msg-u" key={i} data-testid="ask-revq-user-msg">{m.t}</div>
                  ) : (
                    <AssistantMsg key={i} cpt={m.cpt} />
                  )
                )
              ) : (
                <div className="ar-empty" data-testid="ask-revq-empty">
                  <div className="ar-empty-t">Ask how to bill anything</div>
                  <div className="ar-empty-s">SHAWL answers from 181k+ real adjudications.</div>
                  <div className="ar-empty-chips">
                    {SUGGESTIONS.map((q) => (
                      <button key={q} className="ar-empty-chip" onClick={() => send(q)} data-testid="ask-revq-suggestion">{q}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="ar-composer">
              <div className="ar-cbox">
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Ask how to bill a CPT, a combination, a payer…"
                  data-testid="ask-revq-input"
                />
                <button className="ar-send" onClick={() => send(input)} data-testid="ask-revq-send">
                  <Send size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button className="arq-fab" data-testid="ask-revq-fab" onClick={() => setOpen(true)} aria-label="Ask revQ" title="Ask revQ (⌘J)">
          <img src="/revq-emblem-white.png" alt="" className="arq-fab-mark" />
          <span className="arq-fab-label">Ask revQ</span>
          <span className="arq-fab-kbd">⌘J</span>
        </button>
      )}
    </>
  );
}
